import Animations from '@Front/animations';
import Component from '@bythepixel/component-loader';
import { Confetti } from '@Helpers';
import gsap from 'gsap';
import { DEFAULTS, confettiOptions } from './config';

export default class FormController extends Component {
  static selector = '[data-comp-form-controller]';

  constructor(containerEl, options = DEFAULTS) {
    super(containerEl, options);

    this.classes = this.$options.classes;
    this.selectors = this.$options.selectors;

    // Form elements
    this.form = this.$container.querySelector(this.selectors.form);
    this.fields = this.form.querySelectorAll(this.selectors.field);
    this.textarea = this.form.querySelector(this.selectors.textarea);
    this.messageContainers = this.form.querySelectorAll(this.selectors.messageContainer);
    this.successMessage = this.form.querySelector(this.selectors.successMessage);

    // Form button elements
    this.button = this.form.querySelector(this.selectors.button);
    this.buttonIcon = this.button.querySelector(this.selectors.buttonIcon);
    this.buttonText = this.button.querySelector(this.selectors.buttonText);
    this.buttonLoader = this.button.querySelector(this.selectors.buttonLoader);

    // Other
    this.endpoint = this.form.dataset.formSubmissionRoute;
    this.secureInputId = `${this.$id}_secureInput`;
    this.confettiCanvas = this.$container.querySelector(this.selectors.confettiCanvas);
    this.confetti = new Confetti(this.confettiCanvas);
    this.baseFormContent = this.$container.querySelector(this.selectors.baseFormContent);
    this.postSubmitContent = this.$container.querySelector(this.selectors.postSubmitContent);
    this.isNewsletterSignup = this.form.id === 'newsletter-signup';

    this.mount();
  }

  showPostSubmitContent() {
    const { baseFormContent, postSubmitContent } = this;

    baseFormContent.classList.add('hidden');
    baseFormContent.setAttribute('aria-hidden', true);

    postSubmitContent.classList.remove('hidden');
    postSubmitContent.setAttribute('aria-hidden', false);
  }

  appendSecureInput() {
    if (document.getElementById(this.secureInputId)) {
      return;
    }

    const input = document.createElement('input');

    input.type = 'hidden';
    input.name = 'secure';
    input.value = 'secure';
    input.id = this.secureInputId;

    this.form.appendChild(input);
  }

  #attachListeners() {
    this.form.addEventListener('submit', async e => {
      e.preventDefault();
      await this.submitForm(e);
    });

    if (this.textarea) {
      this.textarea.style.boxSizing = 'border-box';
      const offset = this.textarea.offsetHeight - this.textarea.clientHeight;
      this.textarea.addEventListener('input', (event) => {
        event.target.style.height = 'auto';
        event.target.style.height = `${event.target.scrollHeight + offset}px`;
      });
    }
  }

  handleValidationErrors(formErrors) {
    Object.keys(formErrors).forEach(fieldName => {
      const field = this.$container.querySelector(`[data-comp-form-field="${fieldName}"]`);

      if (field) {
        field.classList.add(this.classes.invalidField);
      }

      formErrors[fieldName].forEach(err => {
        const messageField = this.$container.querySelector(`[data-comp-form-message="${fieldName}"]`);

        if (messageField) {
          messageField.classList.add(this.classes.visibleMessageField);
          this.renderMessage(messageField, err, 'error');
        }
      });
    });
  }

  renderMessage(container, err, level) {
    container.insertAdjacentHTML('beforeend', this.getMessageHTML(err, level));
  }

  resetButtonText() {
    this.hideButtonLoader();
    this.showButtonIcon();
  }

  showServerPendingMsg() {
    this.hideButtonIcon();
    this.showButtonLoader();
  }

  showServerSuccessMsg(data) {
    // Show success message and confetti
    this.successMessage.style.display = 'block';
    const message = data.message ?? 'Your message has been sent.';
    this.renderMessage(this.successMessage, message, 'success');

    // Hide message after 5s
    setTimeout(() => this.successMessage.style.display = 'none', 5000);
  }

  async submitForm(e) {
    this.clearFieldsAndMessages();
    this.disableForm();
    this.appendSecureInput();

    const formData = new FormData(e.target);
    const { endpoint } = this;

    this.showServerPendingMsg();

    fetch(endpoint, {
      method: 'post',
      body: formData,
    }).then(resp => this.handleServerResponse(resp));

    this.enableForm();
    this.resetButtonText();
  }

  async handleServerResponse(resp) {
    const data = await resp.json();
    const { errors } = data;
    const { status } = resp;

    if (status === 422 || status === 400) {
      this.handleValidationErrors(errors);
      return;
    }

    if (status !== 200) {
      return;
    }

    this.popConfetti();

    // we don't replace the content on the newsletter form
    if (this.isNewsletterSignup) {
      this.showServerSuccessMsg(data);
      this.disableForm();
      return;
    }

    this.showPostSubmitContent();
    this.hideForm();
  }

  #attachSectionObservers() {
    const options = {
      rootMargin: '0px',
      threshold: 0.2,
    };
    const elements = [
      this.$container.querySelector('.contact-form__overline'),
      this.$container.querySelector('.contact-form__subtitle'),
      this.$container.querySelector('.contact-form__header'),
      this.$container.querySelector('.contact-form'),
    ];

    const headerCallback = () => {
      const emoji = document.querySelector('.contact-form .emoji');
      if (!emoji) return;

      const wordMask = emoji.querySelector('.word__mask');

      setTimeout(() => {
        if (wordMask) wordMask.remove();

        emoji.classList.add('triggered');
      }, 1000);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const { target } = entry;

        if (
          entry.isIntersecting
          && entry.intersectionRatio >= options.threshold
          && target.classList.contains('invisible')
          && !target.dataset.animated
        ) {
          target.dataset.animated = true;
          if (target.classList.contains('contact-form__header')) {
            Animations.maskRevealText(target, {}, headerCallback);
          } else {
            Animations.maskRevealText(target);
          }
        }
      });
    }, options);

    elements.forEach((element) => {
      if (!element) return;

      observer.observe(element);
    });
  }

  #attachFormObservers() {
    const fieldsThreshold = 0.6;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const { isIntersecting, intersectionRatio } = entry;
        if (isIntersecting && intersectionRatio >= fieldsThreshold) {
          this.#animateFields();
        }
      });
    }, {
      rootMargin: '0px',
      threshold: fieldsThreshold,
    });

    observer.observe(this.$container);
  }

  #animateFields() {
    this.fields.forEach((field, index) => {
      if (field.classList.contains('revealed')) return;
      const label = field.querySelector('label .element-mask');
      const stagger = index * 100;

      setTimeout(() => {
        field.classList.add('revealed');
        label.classList.remove('invisible');
        gsap.fromTo(label, {
          y: 200,
        }, {
          y: 0,
          ease: 'power2',
        });
      }, stagger);
    });
  }

  showButtonIcon() {
    this.buttonIcon.classList.remove('hidden');
  }

  hideButtonIcon() {
    this.buttonIcon.classList.add('hidden');
  }

  showButtonLoader() {
    this.buttonLoader.classList.remove('hidden');
  }

  hideButtonLoader() {
    this.buttonLoader.classList.add('hidden');
  }

  popConfetti() {
    this.confetti.pop(confettiOptions.particles);
  }

  changeButtonText(text) {
    this.buttonText.innerText = text;
  }

  clearFieldsAndMessages() {
    this.messageContainers.forEach(el => {
      while (el.children[1] && el.childElementCount > 1) el.removeChild(el.children[1]);
      el.classList.remove(this.classes.visibleMessageField);
    });

    this.fields.forEach(field => field.classList.remove(this.classes.invalidField));
  }

  disableForm() {
    this.button.disabled = true;
  }

  enableForm() {
    this.button.disabled = false;
  }

  hideForm() {
    const { form } = this;

    form.classList.add('hidden');

    this.disableForm();
  }

  getMessageHTML(message, level = 'notice') {
    return `<p class="contact-form-message form-message--${level}">${message}</p>`;
  }

  mount() {
    this.#attachListeners();
    this.#attachFormObservers();
    this.#attachSectionObservers();
  }
}
